import { Component, OnInit, inject } from '@angular/core';
import {
  FormBuilder,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import axios, { AxiosError } from 'axios';
import { HelpersService } from 'src/app/services/helpers/helpers.service';
import { environment } from 'src/environments/environment';
import { WorkflowService } from 'src/app/shared/services/workflow/workflow.service';
import { AdminCaseTableItem } from '../cases.component';
import { PayeePopupComponent } from '../../../../shared/payee-popup/payee-popup.component';
import { PayeeAutocompleteComponent } from '../../../../shared/payee-form-fields/payee-autocomplete/payee-autocomplete.component';
import { PayeeFormFieldComponent } from '../../../../shared/payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeDatepickerComponent } from '../../../../shared/payee-form-fields/payee-datepicker/payee-datepicker.component';
import { PayeeInputDirective } from '../../../../shared/payee-input/payee-input.directive';
import { PayeeButtonComponent } from '../../../../shared/payee-buttons/payee-button/payee-button.component';
import { PayeeDropzoneComponent } from '../../../../shared/payee-dropzone/payee-dropzone.component';
import { PayeeIconComponent } from '../../../../shared/payee-icon/payee-icon.component';
import { PayeeMiniIconButtonComponent } from '../../../../shared/payee-buttons/payee-mini-icon-button/payee-mini-icon-button.component';

type TransitionOption = {
  id: string;
  label: string;
  group: string;
};

type DialogData = {
  transitions: TransitionOption[];
};

function formatTime(date: Date): string {
  const hours = `${date.getHours()}`.padStart(2, '0');
  const minutes = `${date.getMinutes()}`.padStart(2, '0');

  return `${hours}:${minutes}`;
}

function combineDateAndTime(date: Date, time: string): string {
  const [hours, minutes] = time.split(':').map((part) => Number(part));
  const combined = new Date(date);
  combined.setHours(hours, minutes, 0, 0);

  return combined.toISOString();
}

@Component({
  selector: 'app-create-transition-log-dialog',
  templateUrl: './create-transition-log-dialog.component.html',
  styleUrls: ['./create-transition-log-dialog.component.scss'],
  imports: [
    PayeePopupComponent,
    FormsModule,
    ReactiveFormsModule,
    PayeeAutocompleteComponent,
    PayeeFormFieldComponent,
    PayeeDatepickerComponent,
    PayeeInputDirective,
    PayeeButtonComponent,
    PayeeDropzoneComponent,
    PayeeIconComponent,
    PayeeMiniIconButtonComponent,
  ],
})
export class CreateTransitionLogDialogComponent implements OnInit {
  private dialogRef =
    inject<MatDialogRef<CreateTransitionLogDialogComponent>>(MatDialogRef);
  private fb = inject(FormBuilder);
  private helpersService = inject(HelpersService);
  private snackbar = inject(MatSnackBar);
  private workflowService = inject(WorkflowService);

  data = inject<AdminCaseTableItem>(MAT_DIALOG_DATA);

  loading = false;
  dialogData?: DialogData;
  files: File[] = [];

  readonly form = this.fb.group({
    transition: this.fb.control<TransitionOption | null>(null, Validators.required),
    date: this.fb.control<Date | null>(new Date(), Validators.required),
    time: this.fb.control(formatTime(new Date()), {
      validators: [Validators.required, Validators.pattern(/^\d{2}:\d{2}$/)],
      nonNullable: true,
    }),
  });

  async ngOnInit(): Promise<void> {
    try {
      this.loading = true;

      const result = await axios.get(
        `${environment.baseUrl}/api/${this.data.id}/transition-log`,
      );
      this.dialogData = result.data;
      this.loading = false;
    } catch (error) {
      console.error('Error while loading dialog data', error);
      this.cancel();
    }
  }

  async submit(): Promise<void> {
    if (this.form.invalid || this.loading) {
      this.helpersService.markAllChildrenAsDirty(this.form);
      return;
    }

    const { transition, date, time } = this.form.getRawValue();
    if (!transition || !date) {
      return;
    }

    const formData = new FormData();
    formData.append('transition', transition.id);
    formData.append('event_at', combineDateAndTime(date, time));
    for (const file of this.files) {
      formData.append('files[]', file);
    }

    try {
      this.loading = true;

      await axios.post(
        `${environment.baseUrl}/api/${this.data.id}/transition-log`,
        formData,
      );
      this.dialogRef.close(true);
    } catch (error: AxiosError | unknown) {
      console.error('Error while creating transition log', error);
      let errorMessage = 'Ismeretlen hiba';
      if (error instanceof AxiosError && error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      this.snackbar.open(errorMessage, 'OK', { duration: 5000 });
    } finally {
      this.loading = false;
    }
  }

  filesDropped(files: FileList): void {
    for (let i = 0; i < files.length; i++) {
      const file = files.item(i);
      if (file) {
        this.files.push(file);
      }
    }
  }

  removeFile(index: number): void {
    this.files.splice(index, 1);
  }

  readonly transitionDisplayWith = (
    transition?: TransitionOption | null,
  ): string => {
    if (!transition) {
      return '';
    }

    const base = `${transition.group}: ${transition.label}`;
    if (!this.isDuplicateLabel(transition.label)) {
      return base;
    }

    return `${base} (${this.shortTransitionId(transition.id)})`;
  };

  readonly transitionFilterPredicate = (
    option: unknown,
    filter: string,
  ): boolean => {
    const transition = option as TransitionOption;
    if (!transition?.id) {
      return false;
    }

    const needle = filter.toLowerCase();

    return (
      transition.label.toLowerCase().includes(needle)
      || transition.group.toLowerCase().includes(needle)
      || transition.id.toLowerCase().includes(needle)
    );
  };

  isDuplicateLabel(label: string): boolean {
    return (this.dialogData?.transitions ?? []).filter(
      (transition) => transition.label === label,
    ).length > 1;
  }

  private shortTransitionId(id: string): string {
    return id
      .replace(/^App\\Workflow\\Processes\\/, '')
      .replace(/^App\\Workflow\\/, '')
      .replace(/\\Transitions\\/, '\\');
  }

  readonly cancel = (): void => {
    this.dialogRef.close();
  };
}
